// FTAToolbar.tsx — Toolbar actions for the FTA editor (CC ≤ 12)
import React, { useRef, useState } from 'react';
import {
    Plus, Undo2, Redo2, RotateCcw, BarChart3, ListOrdered,
    Palette, FileText, Download, Upload, Moon, Sun, FileJson,
    ChevronDown,
} from 'lucide-react';
import clsx from 'clsx';
import { industryTemplates } from '../../utils/fta/industryTemplates';

export type DisplayMode = 'colorful' | 'basic';

interface Props {
    isDark: boolean;            setIsDark: (v: boolean) => void;
    displayMode: DisplayMode;   setDisplayMode: (v: DisplayMode) => void;
    view: 'diagram' | 'table'; setView: (v: 'diagram' | 'table') => void;
    histIndex: number;          histLength: number;
    onUndo: () => void;         onRedo: () => void;
    onNew: () => void;          onLayout: () => void;    onCalculate: () => void;
    onExportJSON: () => void;   onImport: (f: File) => void;
    onLoadTemplate: (key: string) => void;
    diagramName: string;
}

const TEMPLATE_KEYS = Object.keys(industryTemplates);

const FTAToolbar: React.FC<Props> = ({
    isDark, setIsDark, displayMode, setDisplayMode,
    view, setView, histIndex, histLength,
    onUndo, onRedo, onNew, onLayout, onCalculate,
    onExportJSON, onImport, onLoadTemplate,
}) => {
    const [showTemplates, setShowTemplates] = useState(false);
    const [showExport, setShowExport]       = useState(false);
    const importRef = useRef<HTMLInputElement>(null);
    const tmplRef   = useRef<HTMLDivElement>(null);
    const expRef    = useRef<HTMLDivElement>(null);

    const btn = isDark
        ? 'bg-slate-700 hover:bg-slate-600 text-slate-200 border-slate-600'
        : 'bg-slate-100 hover:bg-slate-200 text-slate-700 border-slate-300';
    const iconBtn = (active: boolean) => clsx('p-2 rounded-lg border transition-colors', active ? btn : clsx(btn, 'opacity-30 cursor-not-allowed'));
    const dropSurface = isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200';
    const dropItem = isDark ? 'hover:bg-slate-700 text-slate-200' : 'hover:bg-slate-50 text-slate-700';

    function handleFile(e: React.ChangeEvent<HTMLInputElement>) {
        const f = e.target.files?.[0];
        if (f) onImport(f);
        e.target.value = '';
    }

    return (
        <div className="flex items-center gap-1.5 shrink-0 ml-auto">
            {/* New */}
            <button onClick={onNew} title="New" className={clsx('p-2 rounded-lg border transition-colors', btn)}><Plus size={15} /></button>

            {/* Undo / Redo */}
            <button onClick={onUndo} disabled={histIndex <= 0} title="Undo" className={iconBtn(histIndex > 0)}><Undo2 size={15} /></button>
            <button onClick={onRedo} disabled={histIndex >= histLength - 1} title="Redo" className={iconBtn(histIndex < histLength - 1)}><Redo2 size={15} /></button>

            {/* Layout + Calculate */}
            <button onClick={onLayout}    title="Auto-layout"      className={clsx('p-2 rounded-lg border transition-colors', btn)}><RotateCcw size={15} /></button>
            <button onClick={onCalculate} title="Calc probabilities" className={clsx('p-2 rounded-lg border transition-colors', btn)}><BarChart3 size={15} /></button>

            {/* Table/Diagram toggle */}
            <button onClick={() => setView(view === 'diagram' ? 'table' : 'diagram')} title="Toggle table view"
                className={clsx('p-2 rounded-lg border transition-colors', view === 'table' ? 'bg-indigo-600 text-white border-indigo-700' : btn)}>
                <ListOrdered size={15} />
            </button>

            <div className="w-px h-5 bg-slate-300 dark:bg-slate-600 mx-0.5" />

            {/* Display mode */}
            <button onClick={() => setDisplayMode(displayMode === 'colorful' ? 'basic' : 'colorful')}
                title="Toggle Basic / Colorful mode"
                className={clsx('flex items-center gap-1 px-2 py-1.5 rounded-lg border text-xs font-semibold transition-colors', btn)}>
                <Palette size={13} />{displayMode === 'colorful' ? 'Colorful' : 'Basic'}
            </button>

            {/* Templates dropdown */}
            <div className="relative" ref={tmplRef}>
                <button onClick={() => setShowTemplates(v => !v)}
                    className={clsx('flex items-center gap-1 px-2 py-1.5 rounded-lg border text-xs font-semibold transition-colors', btn)}>
                    <FileText size={13} /> Templates <ChevronDown size={11} className={clsx('transition-transform', showTemplates && 'rotate-180')} />
                </button>
                {showTemplates && (
                    <div className={clsx('absolute top-full right-0 mt-1 w-56 rounded-xl shadow-2xl border py-1 z-50', dropSurface)}>
                        {TEMPLATE_KEYS.map(k => (
                            <button key={k} onClick={() => { onLoadTemplate(k); setShowTemplates(false); }}
                                className={clsx('w-full text-left px-4 py-2 text-sm transition-colors capitalize', dropItem)}>
                                {k.replace(/([A-Z])/g, ' $1').trim()}
                            </button>
                        ))}
                    </div>
                )}
            </div>

            {/* Export / Import dropdown */}
            <div className="relative" ref={expRef}>
                <button onClick={() => setShowExport(v => !v)}
                    className={clsx('flex items-center gap-1 px-2 py-1.5 rounded-lg border text-xs font-semibold transition-colors', btn)}>
                    <Download size={13} /> Export <ChevronDown size={11} className={clsx('transition-transform', showExport && 'rotate-180')} />
                </button>
                {showExport && (
                    <div className={clsx('absolute top-full right-0 mt-1 w-44 rounded-xl shadow-2xl border py-1 z-50', dropSurface)}>
                        <button onClick={() => { onExportJSON(); setShowExport(false); }}
                            className={clsx('w-full text-left px-4 py-2 text-sm flex items-center gap-2 transition-colors', dropItem)}>
                            <FileJson size={13} className="text-blue-500" /> Export JSON
                        </button>
                        <button onClick={() => { importRef.current?.click(); setShowExport(false); }}
                            className={clsx('w-full text-left px-4 py-2 text-sm flex items-center gap-2 transition-colors', dropItem)}>
                            <Upload size={13} className="text-green-500" /> Import JSON
                        </button>
                    </div>
                )}
                <input ref={importRef} type="file" accept=".json" className="hidden" onChange={handleFile} />
            </div>

            {/* Dark mode */}
            <button onClick={() => setIsDark(!isDark)} title="Toggle dark mode"
                className={clsx('p-2 rounded-lg border transition-colors', btn)}>
                {isDark ? <Sun size={15} /> : <Moon size={15} />}
            </button>
        </div>
    );
};

export default FTAToolbar;
